import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { z } from "zod";
import { toFormikValidationSchema } from "zod-formik-adapter";
import {
  adminPostWeatherReportReqDtoSchema,
  useAdminElevationLevelControllerFindMany,
  useAdminLocationControllerGetAll,
  useAdminWeatherReportControllerCreateWeatherReport,
} from "service";
import {
  AdminLocationWithCustomerResDto,
  AdminPostWeatherReportReqDto,
} from "service/api/estabrookSchemas";
import DashboardContentLayout from "../components/dashboard-content-layout";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Slider,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { Edit } from "@mui/icons-material";
import { AdRoutes } from "../../../constants/ad-routes";
import { makeSxStyles, useDebounce } from "ui";
import { addressIso } from "../../../utils/string-helpers";
import { queryClient, skipGlobalErrorToast } from "../../../query-client";
import { queryKeyFn } from "service/api/estabrookContext";
import Navbar from "../../../components/navbar/navbar";
import HeaderItems, {
  HeaderItemsRight,
} from "../../../shared/components/header-items/HeaderItems";
import { HeaderItemsLeft } from "../../../shared/components/header-items/HeaderItems";
import useUnsavedChangesBlocker from "../../../shared/hooks/useUnsavedChangesBlocker";
import WeatherSeveritySelector, {
  WeatherSeverity,
} from "../snow-reports/components/weather-severity-selector";
import ElevationLevelSliders, {
  ElevationLevelValue,
  snowValueFieldSx,
} from "../snow-reports/components/elevation-level-sliders";
import EditElevationLevelsModal from "../snow-reports/components/edit-elevation-levels-modal";

type ReportTab = "address" | "elevation";

interface ElevationFormValues {
  severity: WeatherSeverity;
  measurementSystem: "imperial";
  levels: ElevationLevelValue[];
}

const ADDRESS_REQUIRED_MESSAGE = "Select an address to create this report.";

// locationId is optional on the generated DTO (it's only required for
// location-based reports), so make it required client-side for this tab.
const addressFormValidationSchema = adminPostWeatherReportReqDtoSchema.extend({
  locationId: z.string().min(1, ADDRESS_REQUIRED_MESSAGE),
});

// Backend 400s can still concatenate raw class-validator output (ESR-1647) -
// never surface a message that dumps a DTO field name.
const isFieldValidatorDump = (detail?: string) =>
  !!detail && /\blocationId\b|\belevationLevels\b/.test(detail);

const GENERIC_CREATE_ERROR = "Couldn't create the report. Please try again.";

const SnowReportsCreatePage = () => {
  const navigate = useNavigate();
  const styles = useStyles();
  const [tab, setTab] = useState<ReportTab>("address");
  const [search, setSearch] = useState<string>("");
  const [srch, setSrch] = useState<string>("");
  const [selected, setSelected] = useState<AdminLocationWithCustomerResDto>();
  const [levelsModalOpen, setLevelsModalOpen] = useState(false);
  const { debounce } = useDebounce(475);

  const onSearch = (value: string) => {
    debounce(() => setSearch(value));
  };

  const { data: locationsData, isLoading: isLoadingLocations } =
    useAdminLocationControllerGetAll(
      {
        queryParams: {
          page: 1,
          limit: 5,
          search,
          sort: "address",
        },
      },
      {
        enabled: search.length === 0 || search.length > 2,
        keepPreviousData: false,
      },
    );

  const { data: elevationLevelsData, isLoading: isLoadingLevels } =
    useAdminElevationLevelControllerFindMany({ queryParams: { limit: 100 } });

  const { mutate, isLoading } =
    useAdminWeatherReportControllerCreateWeatherReport({
      // This reports its own failure below (a friendly message, never the raw
      // validator dump the global toast would show), so opt out of that.
      meta: skipGlobalErrorToast,
      onSuccess: () => {
        toast.success("Report Created Successfully");
        addressForm.resetForm();
        elevationForm.resetForm();
        queryClient.invalidateQueries(
          queryKeyFn({
            path: "/api/v1/admin/weather-reports",
            operationId: "adminWeatherReportControllerGetAll",
            variables: {},
          }),
        );
        setTimeout(() => {
          navigate(AdRoutes.snowReports);
        }, 10);
      },
      onError: (error) => {
        const detail = error?.errors?.[0]?.detail;
        toast.error(
          detail && !isFieldValidatorDump(detail)
            ? detail
            : GENERIC_CREATE_ERROR,
        );
      },
    });

  const addressForm = useFormik<AdminPostWeatherReportReqDto>({
    initialValues: {
      severity: "rain",
      snowAmount: 0,
      measurementSystem: "imperial",
      locationId: "",
    },
    validationSchema: toFormikValidationSchema(addressFormValidationSchema),
    onSubmit: (values) => mutate({ body: { ...values } }),
  });

  const elevationForm = useFormik<ElevationFormValues>({
    initialValues: {
      severity: "rain",
      measurementSystem: "imperial",
      levels: [],
    },
    onSubmit: (values) =>
      mutate({
        body: {
          severity: values.severity,
          measurementSystem: values.measurementSystem,
          elevationLevels: values.levels.map((level) => ({
            min: level.min,
            max: level.max,
            snowAmount: level.snowAmount,
          })),
        },
      }),
  });

  // Seed elevation rows from the org's configured levels, preserving any snow
  // amounts already entered for levels that still exist (matched by id).
  useEffect(() => {
    const orgLevels = elevationLevelsData?.data ?? [];
    elevationForm.resetForm({
      values: {
        severity: elevationForm.values.severity,
        measurementSystem: "imperial",
        levels: orgLevels.map((level) => {
          const existing = elevationForm.values.levels.find(
            (row) => row.id === level.id,
          );
          return {
            id: level.id,
            min: level.min,
            max: level.max,
            snowAmount: existing?.snowAmount ?? 0,
          };
        }),
      },
    });
  }, [elevationLevelsData]);

  const isElevationTab = tab === "elevation";

  useUnsavedChangesBlocker(
    isElevationTab ? elevationForm.dirty : addressForm.dirty,
  );

  const createDisabled = isLoading
    ? true
    : isElevationTab
      ? elevationForm.values.levels.length === 0 || !elevationForm.isValid
      : !addressForm.dirty || !addressForm.isValid;

  const onCreate = () =>
    isElevationTab ? elevationForm.submitForm() : addressForm.submitForm();

  return (
    <DashboardContentLayout>
      <Navbar showAccountMenu>
        <HeaderItems>
          <HeaderItemsLeft
            breadcrumbs={[
              {
                label: "Snow Reports",
                url: AdRoutes.snowReports,
              },
              {
                label: "Create Snow Report",
                url: AdRoutes.snowReportsCreate,
              },
            ]}
          />
          <HeaderItemsRight>
            <Button
              disabled={createDisabled}
              variant="contained"
              onClick={onCreate}
              data-testid="create-report-button"
              startIcon={
                isLoading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : undefined
              }
            >
              {isLoading ? "Creating..." : "Create Report"}
            </Button>
          </HeaderItemsRight>
        </HeaderItems>
      </Navbar>
      <Tabs
        value={tab}
        onChange={(_e, value: ReportTab) => setTab(value)}
        aria-label="Snow report type"
        sx={styles.tabs}
      >
        <Tab label="By Address" value="address" data-testid="tab-by-address" />
        <Tab
          label="By Elevation"
          value="elevation"
          data-testid="tab-by-elevation"
        />
      </Tabs>
      <Box sx={styles.customerDetailContainer}>
        <Typography fontWeight={600} alignSelf="flex-start">
          Weather Severity
        </Typography>
        {isElevationTab ? (
          <WeatherSeveritySelector
            value={elevationForm.values.severity}
            onChange={(severity) =>
              elevationForm.setFieldValue("severity", severity)
            }
          />
        ) : (
          <WeatherSeveritySelector
            value={addressForm.values.severity}
            onChange={(severity) =>
              addressForm.setFieldValue("severity", severity)
            }
          />
        )}

        {isElevationTab ? (
          <>
            <Typography fontWeight={600} alignSelf="flex-start">
              Elevation Levels
            </Typography>
            <Box>
              <Button
                variant="outlined"
                startIcon={<Edit />}
                onClick={() => setLevelsModalOpen(true)}
                data-testid="edit-elevation-levels-button"
              >
                Edit
              </Button>
            </Box>
            {isLoadingLevels ? (
              <Box sx={styles.levelsLoader}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              <ElevationLevelSliders
                levels={elevationForm.values.levels}
                onChange={(index, snowAmount) =>
                  elevationForm.setFieldValue(
                    `levels.${index}.snowAmount`,
                    snowAmount,
                  )
                }
              />
            )}
          </>
        ) : (
          <>
            <Typography fontWeight={600}>Current Snowfall</Typography>
            <Box
              display="flex"
              flexDirection="row"
              alignItems="center"
              gap="24px"
            >
              <TextField
                type="number"
                inputProps={{
                  style: { textAlign: "center" },
                  min: 0,
                  max: 40,
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">″</InputAdornment>
                  ),
                }}
                sx={snowValueFieldSx}
                value={addressForm.values.snowAmount ?? 0}
                onChange={(e) => {
                  const raw = e.target.value;
                  const n = raw === "" ? 0 : Number(raw);
                  addressForm.setFieldValue(
                    "snowAmount",
                    Math.max(0, Math.min(40, n)),
                  );
                }}
              />
              <Slider
                id="snowAmount"
                value={addressForm.values.snowAmount ?? 0}
                max={40}
                min={0}
                step={1}
                onChange={(_e, value) =>
                  addressForm.setFieldValue("snowAmount", value)
                }
              />
            </Box>
            <Typography fontWeight={600}>Address</Typography>
            <Autocomplete
              id="locationId"
              // Controlled: this tab unmounts when switching to By Elevation,
              // and an uncontrolled Autocomplete loses its selection on
              // remount. `selected` survives the tab switch, so this reseeds it.
              value={selected ?? null}
              isOptionEqualToValue={(option, val) => option.id === val.id}
              options={locationsData?.data || []}
              getOptionLabel={(option) => addressIso(option)}
              noOptionsText={
                isLoadingLocations
                  ? search.length > 0
                    ? "Searching..."
                    : "Loading..."
                  : "No options"
              }
              onChange={(_e, value) => {
                setSelected(value as AdminLocationWithCustomerResDto);
                addressForm.setFieldValue("locationId", value?.id || "");
                // Skip re-validating here - it'd run against the pre-update
                // values (stale, in the same handler) and flash a bogus error.
                // The setFieldValue call above already validates correctly.
                addressForm.setFieldTouched("locationId", true, false);
              }}
              renderInput={(params) => (
                <TextField
                  placeholder="Search for specific addresses..."
                  onChange={(e) => {
                    const value = e.target.value;
                    onSearch(value);
                    setSrch(value);
                  }}
                  onBlur={() => addressForm.setFieldTouched("locationId", true)}
                  value={srch}
                  error={
                    addressForm.touched.locationId &&
                    !!addressForm.errors.locationId
                  }
                  helperText={
                    addressForm.touched.locationId &&
                    addressForm.errors.locationId
                  }
                  {...params}
                />
              )}
            />
            <Typography fontWeight={600}>Customer</Typography>
            <Typography color="gray" data-testid="customer-name">
              {selected && `${selected.firstName} ${selected.lastName}`}
            </Typography>
          </>
        )}
      </Box>
      <EditElevationLevelsModal
        open={levelsModalOpen}
        onClose={() => setLevelsModalOpen(false)}
        levels={elevationLevelsData?.data ?? []}
      />
    </DashboardContentLayout>
  );
};

const useStyles = makeSxStyles(() => ({
  tabs: {
    mt: "25px",
    minHeight: "49px",
    borderBottom: "1px solid #333537",
    "& .MuiTabs-indicator": {
      height: "3px",
      borderRadius: "3px 3px 0 0",
      backgroundColor: "#8ACEFF",
    },
    "& .MuiTab-root": {
      textTransform: "none",
      fontFamily: "Inter, sans-serif",
      fontWeight: 500,
      fontSize: "14px",
      lineHeight: "20px",
      letterSpacing: "0.1px",
      minHeight: "49px",
      minWidth: "auto",
      padding: "14px 16px",
      color: "#C1C7CE",
      "&.Mui-selected": {
        color: "#8ACEFF",
      },
    },
  },
  customerDetailContainer: {
    mt: "40px",
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    rowGap: "40px",
    maxWidth: "50%",
    alignItems: "center",
  },
  levelsLoader: {
    gridColumn: "1 / -1",
    display: "flex",
    justifyContent: "center",
    py: "8px",
  },
}));

export default SnowReportsCreatePage;
